import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle, ArrowUpDown, ChevronRight, Users,
} from 'lucide-react'
import { clsx } from 'clsx'
import { PageHeader } from '../components/ui/PageHeader'
import { useCustomers, type CustomerSummary } from '../hooks/useCustomers'

type SortKey = 'revenue_ytd' | 'margin_pct' | 'dso_days' | 'backlog' | 'overdue' | 'concentration_pct'

function fmt(n: number) {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)
}

function SortableHeader({
  label, sortKey, current, onSort,
}: { label: string; sortKey: SortKey; current: { key: SortKey; asc: boolean }; onSort: (k: SortKey) => void }) {
  const active = current.key === sortKey
  return (
    <button
      className={clsx(
        'flex items-center gap-1 text-xs font-semibold uppercase tracking-wide transition-colors',
        active ? 'text-indigo-400' : 'text-[--km-text-muted] hover:text-[--km-text-secondary]',
      )}
      onClick={() => onSort(sortKey)}
    >
      {label}
      <ArrowUpDown size={11} className={active ? 'opacity-100' : 'opacity-40'} />
    </button>
  )
}

function RiskBadge({ overdue, receivables }: { overdue: number; receivables: number }) {
  if (receivables === 0) return null
  const pct = (overdue / receivables) * 100
  if (pct < 10) return null
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold text-red-400 ring-1 ring-red-500/20">
      <AlertTriangle size={9} />
      Scaduto
    </span>
  )
}

export function Customers() {
  const navigate = useNavigate()
  const { data: customers, isLoading, error } = useCustomers()
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({ key: 'revenue_ytd', asc: false })
  const [search, setSearch] = useState('')

  function handleSort(key: SortKey) {
    setSort(prev => ({ key, asc: prev.key === key ? !prev.asc : false }))
  }

  const filtered = (customers ?? [])
    .filter(c => c.customer_name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const va = a[sort.key] ?? 0
      const vb = b[sort.key] ?? 0
      return sort.asc ? (va as number) - (vb as number) : (vb as number) - (va as number)
    })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Clienti"
        subtitle="Analisi KPI per cliente — revenue, margine, DSO, backlog, contratti ricorrenti"
        icon={Users}
        iconColor="from-violet-500 to-purple-600"
      />

      {/* Search */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder="Cerca cliente..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={clsx(
            'w-full max-w-xs rounded-xl border border-white/10 bg-white/[0.04]',
            'px-4 py-2.5 text-sm text-[--km-text-primary] placeholder:text-[--km-text-disabled]',
            'focus:outline-none focus:ring-1 focus:ring-indigo-500/50 transition-all',
          )}
        />
        <span className="text-sm text-[--km-text-muted]">{filtered.length} clienti</span>
      </div>

      {/* Table */}
      <div className="km-panel overflow-hidden">
        {isLoading && (
          <div className="flex items-center justify-center py-16 text-[--km-text-muted] text-sm">
            Caricamento dati clienti...
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center py-16 text-red-400 text-sm">
            Errore nel caricamento. Riprova.
          </div>
        )}
        {!isLoading && !error && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-[--km-text-muted]">
                    Cliente
                  </th>
                  <th className="px-4 py-4 text-right">
                    <SortableHeader label="Revenue YTD" sortKey="revenue_ytd" current={sort} onSort={handleSort} />
                  </th>
                  <th className="px-4 py-4 text-right">
                    <SortableHeader label="Margine" sortKey="margin_pct" current={sort} onSort={handleSort} />
                  </th>
                  <th className="px-4 py-4 text-right">
                    <SortableHeader label="Backlog" sortKey="backlog" current={sort} onSort={handleSort} />
                  </th>
                  <th className="px-4 py-4 text-right">
                    <SortableHeader label="Crediti" sortKey="overdue" current={sort} onSort={handleSort} />
                  </th>
                  <th className="px-4 py-4 text-right">
                    <SortableHeader label="DSO" sortKey="dso_days" current={sort} onSort={handleSort} />
                  </th>
                  <th className="px-4 py-4 text-right">
                    <SortableHeader label="Concentr." sortKey="concentration_pct" current={sort} onSort={handleSort} />
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-semibold uppercase tracking-wide text-[--km-text-muted]">
                    Contratti
                  </th>
                  <th className="px-4 py-4" />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-12 text-center text-sm text-[--km-text-muted]">
                      Nessun cliente trovato.
                    </td>
                  </tr>
                )}
                {filtered.map((c: CustomerSummary) => (
                  <tr
                    key={c.customer_name}
                    onClick={() => navigate(`/clienti/${encodeURIComponent(c.customer_name)}`)}
                    className="cursor-pointer transition-colors hover:bg-white/[0.03] group"
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-[--km-text-primary]">{c.customer_name}</span>
                        <RiskBadge overdue={c.overdue} receivables={c.receivables_open} />
                      </div>
                      <span className="text-[11px] text-[--km-text-muted]">
                        {c.active_orders} {c.active_orders === 1 ? 'commessa attiva' : 'commesse attive'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right font-semibold tabular-nums text-[--km-text-primary]">
                      {fmt(c.revenue_ytd)}
                    </td>
                    <td className="px-4 py-4 text-right tabular-nums">
                      <span className={clsx(
                        'font-semibold',
                        c.margin_pct >= 25 ? 'text-emerald-400'
                        : c.margin_pct >= 15 ? 'text-amber-400'
                        : 'text-red-400',
                      )}>
                        {c.margin_pct.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right tabular-nums text-[--km-text-secondary]">
                      {fmt(c.backlog)}
                    </td>
                    <td className="px-4 py-4 text-right tabular-nums">
                      {c.overdue > 0 ? (
                        <span className="text-red-400 font-semibold">{fmt(c.overdue)}</span>
                      ) : (
                        <span className="text-[--km-text-muted]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right tabular-nums">
                      {c.dso_days !== null ? (
                        <span className={clsx(
                          c.dso_days <= 30 ? 'text-emerald-400'
                          : c.dso_days <= 60 ? 'text-amber-400'
                          : 'text-red-400',
                        )}>
                          {c.dso_days}g
                        </span>
                      ) : (
                        <span className="text-[--km-text-muted]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right tabular-nums">
                      <span className={clsx(
                        'text-xs font-semibold',
                        c.concentration_pct >= 30 ? 'text-amber-400'
                        : 'text-[--km-text-secondary]',
                      )}>
                        {c.concentration_pct.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {c.active_contracts > 0 ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-2.5 py-1 text-xs font-semibold text-indigo-400 ring-1 ring-indigo-500/20">
                          {c.active_contracts} attivi
                        </span>
                      ) : (
                        <span className="text-[--km-text-muted] text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <ChevronRight
                        size={14}
                        className="text-[--km-text-muted] group-hover:text-indigo-400 transition-colors"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
